import { useMemo } from "react";
import { CircularProgress, Box } from "@mui/material";
import { UIMessage } from "ai";
import { ChatbotReferenceMetadata } from "../../shared/models/chatbot-references.model";
import { useCreateInternalStateBranch } from "../../state/InternalContext";
import { InternalState } from "../../state/state-models";

type ChatbotMessageReferencesProps = {
    message: UIMessage<ChatbotReferenceMetadata>;
    templateContent: React.ReactNode;
    isLastAssistant: boolean;
    chatStatus: "ready" | "submitted" | "streaming" | "error";
    messageText: string;
};

function buildResponseStoredItem(
    message: UIMessage<ChatbotReferenceMetadata>,
    metadata: ChatbotReferenceMetadata,
): InternalState["storedItems"] {
    const stats = metadata.stats;
    return {
        _response: {
            value: message.id,
            position: 1,
            props: {
                info: {
                    referenceCount: [String(metadata.referenceCount)],
                    fetchedContextChunkCount: [String(stats.fetchedContextChunks.length)],
                    selectedContextChunkCount: [String(stats.selectedContextChunks.length)],
                    fetchedReferenceChunkCount: [String(stats.fetchedReferenceChunks.length)],
                    selectedReferenceChunkCount: [String(stats.selectedReferenceChunks.length)],
                },
            },
        },
    };
}

/**
 * Per-assistant-message reference slot: injects `_references` / `_response` branch state
 * and renders the authored ReferencesTemplate children when metadata is available.
 */
export function ChatbotMessageReferences({
    message,
    templateContent,
    isLastAssistant,
    chatStatus,
    messageText,
}: ChatbotMessageReferencesProps): React.ReactElement | null {
    const createInternalStateBranch = useCreateInternalStateBranch();
    const metadata = message.metadata;

    const branchState = useMemo(() => {
        if (!metadata || metadata.referenceCount === 0) {
            return null;
        }
        return {
            storedArrays: {
                _references: metadata.references,
            },
            storedItems: buildResponseStoredItem(message, metadata),
        };
    }, [message, metadata]);

    const BranchComponent = useMemo(
        () => (branchState ? createInternalStateBranch(branchState) : null),
        [branchState, createInternalStateBranch],
    );

    const inFlight = isLastAssistant && (chatStatus === "streaming" || chatStatus === "submitted");

    if (!metadata) {
        if (inFlight && messageText.length > 0) {
            return (
                <Box sx={{ display: "flex", justifyContent: "flex-start", py: 1 }} aria-busy="true" aria-label="Loading references">
                    <CircularProgress size={20} color="inherit" />
                </Box>
            );
        }
        return null;
    }

    if (!BranchComponent) {
        return null;
    }

    return (
        <BranchComponent>
            {templateContent}
        </BranchComponent>
    );
}
